import dayjs from "dayjs";
import {
  formatDate,
  formatDateRange,
  formatDateTime,
} from "../../UI/Utils/formatDate.ts";
import {
  HistoryCloserDetailDTO,
  HistoryCloserSummaryDTO,
  HistoryDayDTO,
  HistoryGranularity,
  HistoryPeriodSummaryDTO,
  HistorySlotCountsDTO,
  HistorySlotTransitionsDTO,
} from "../../HubSpot/useGetHubSpotCloserAvailabilityHistory.ts";

export const NO_TEAM_LABEL = "Senza team";

export const DAY_NAMES = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

export type PeriodTotals = {
  opening: number;
  closure: number;
  calls: number;
  ooo: number;
  joined: number;
  left: number;
};

export type CloserMembership = "present" | "joined" | "left" | "left_during";

export function teamLabel(team: string | null): string {
  return team ?? NO_TEAM_LABEL;
}

export function filterByTeam<T extends { team: string | null }>(
  closers: T[],
  team: string | null,
): T[] {
  return team === null
    ? closers
    : closers.filter((c) => teamLabel(c.team) === team);
}

export function sumTotals(closers: HistoryCloserSummaryDTO[]): PeriodTotals {
  return closers.reduce<PeriodTotals>(
    (acc, c) => ({
      opening: acc.opening + (c.opening?.effective ?? 0),
      closure: acc.closure + (c.closure?.effective ?? 0),
      calls: acc.calls + (c.closure?.inCall ?? 0),
      ooo: acc.ooo + (c.closure?.ooo ?? 0),
      joined: acc.joined + (!c.opening && c.closure ? 1 : 0),
      left: acc.left + (c.opening && !c.closure ? 1 : 0),
    }),
    { opening: 0, closure: 0, calls: 0, ooo: 0, joined: 0, left: 0 },
  );
}

export function sumTransitions(
  closers: HistoryCloserDetailDTO[],
): HistorySlotTransitionsDTO {
  return closers.reduce<HistorySlotTransitionsDTO>(
    (acc, c) => ({
      booked: acc.booked + (c.transitions?.booked ?? 0),
      blocked: acc.blocked + (c.transitions?.blocked ?? 0),
      callBlocked: acc.callBlocked + (c.transitions?.callBlocked ?? 0),
      freed: acc.freed + (c.transitions?.freed ?? 0),
      callCancelled: acc.callCancelled + (c.transitions?.callCancelled ?? 0),
    }),
    { booked: 0, blocked: 0, callBlocked: 0, freed: 0, callCancelled: 0 },
  );
}

export function membershipOf(closer: HistoryCloserDetailDTO): CloserMembership {
  if (!closer.opening) return "joined";
  if (!closer.closure) return "left";
  const leftDuring = closer.days?.some((d) => d.opening && !d.closure);
  return leftDuring ? "left_during" : "present";
}

export function groupByTeam<T extends { team: string | null }>(
  closers: T[],
  teams: string[],
): { team: string; closers: T[] }[] {
  return [...teams, NO_TEAM_LABEL]
    .map((team) => ({
      team,
      closers: closers.filter((c) => teamLabel(c.team) === team),
    }))
    .filter((g) => g.closers.length > 0);
}

export function saturation(calls: number, effective: number): number {
  return effective > 0 ? Math.round((calls / effective) * 100) : 0;
}

export function deltaPct(closure: number, opening: number): number | null {
  return opening > 0 ? ((closure - opening) / opening) * 100 : null;
}

export function formatDelta(n: number): string {
  return n > 0 ? `+${n}` : `${n}`;
}

export function formatPct(p: number | null): string {
  if (p === null) return "—";
  return `${p > 0 ? "+" : ""}${p.toFixed(1).replace(".", ",")}%`;
}

export function deltaClass(n: number): string {
  if (n > 0) return "text-green-700";
  if (n < 0) return "text-red-600";
  return "text-gray-400";
}

export function nameFromEmail(email: string): string {
  return email
    .split("@")[0]
    .split(".")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export function periodLabel(
  granularity: HistoryGranularity,
  period: Pick<HistoryPeriodSummaryDTO, "periodStart" | "periodEnd">,
): string {
  if (granularity === "month") {
    return new Date(`${period.periodStart}T12:00:00`).toLocaleDateString(
      "it-IT",
      { month: "long", year: "numeric" },
    );
  }
  if (granularity === "day") {
    return formatDate(period.periodStart, { withWeekday: true, locale: "it" });
  }
  return formatDateRange(period.periodStart, period.periodEnd, {
    locale: "it",
  });
}

export function periodShortLabel(
  granularity: HistoryGranularity,
  periodStart: string,
): string {
  return granularity === "month"
    ? dayjs(periodStart).format("MM/YYYY")
    : dayjs(periodStart).format("DD/MM");
}

export function dayLabel(date: string): string {
  const d = dayjs(date);
  return `${DAY_NAMES[d.day()]} ${d.format("DD/MM")}`;
}

export function formatTakenAt(takenAt: string | null): string {
  if (!takenAt) return "non ancora disponibile";
  return formatDateTime(takenAt, { withWeekday: true, locale: "it" });
}

export function sumCloserDays(
  closers: HistoryCloserDetailDTO[],
): HistoryDayDTO[] {
  const byDate = new Map<string, HistoryDayDTO>();
  const add = (
    target: HistorySlotCountsDTO | null,
    source: HistorySlotCountsDTO,
  ): HistorySlotCountsDTO => ({
    free: (target?.free ?? 0) + source.free,
    inCall: (target?.inCall ?? 0) + source.inCall,
    ooo: (target?.ooo ?? 0) + source.ooo,
    effective: (target?.effective ?? 0) + source.effective,
  });
  for (const closer of closers) {
    for (const day of closer.days ?? []) {
      const entry = byDate.get(day.date) ?? {
        date: day.date,
        opening: null,
        closure: null,
      };
      if (day.opening) entry.opening = add(entry.opening, day.opening);
      if (day.closure) entry.closure = add(entry.closure, day.closure);
      byDate.set(day.date, entry);
    }
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}
